import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey,
  date
} from "drizzle-orm/pg-core";
import { carriers } from './carriers';
import { organizations } from './organizations';
import { stateComplianceRules } from './compliance';
import { contacts } from './contacts';
import { documentTemplatePackages, documents, packageVersions } from './documents';
import { docusignEnvelopes } from './docusign';
import { accountingStatusEnum, claimStatusEnum, claimTypeEnum, complianceRequirementTypeEnum, complianceStatusEnum, evaluationStatusEnum, evaluationTypeEnum, feeTypeEnum, fileTypeEnum, inspectionStatusEnum, intakeDecisionPathwayEnum, licenseStatusEnum, negotiationStatusEnum, paRoleEnum, packageGenerationStatusEnum, perilTypeEnum, policyCoverageEnum, propertyTypeEnum, timelineEventTypeEnum } from './enums';
import { feeConfirmations } from './financial';
import { leads } from './leads';
import { tasks } from './tasks';
import { users } from './users';

// Claims table
export const claims = pgTable("claims", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimNumber: varchar("claim_number").unique(), // From carrier paperwork - can be added later
  policyNumber: varchar("policy_number"), // From carrier paperwork - can be added later
  
  // File Type Classification
  fileType: fileTypeEnum("file_type").notNull().default("public_adjuster"), // Distinguish PA files from Appraisal files
  
  dateOfLoss: date("date_of_loss").notNull(),
  dateReported: date("date_reported").defaultNow(),
  perilType: perilTypeEnum("peril_type").notNull(),
  status: claimStatusEnum("status").notNull().default("evaluation"),
  accountingStatus: accountingStatusEnum("accounting_status").notNull().default("no_accounting_activity"),
  description: text("description"),
  estimatedValue: decimal("estimated_value", { precision: 12, scale: 2 }),
  settlementValue: decimal("settlement_value", { precision: 12, scale: 2 }),
  assignedAdjusterId: varchar("assigned_adjuster_id").references(() => users.id),
  
  // Carrier Information (from carrier paperwork)
  carrierId: uuid("carrier_id").references(() => carriers.id),

  claimFiledDate: date("claim_filed_date"),
  
  // Policyholder Information (from carrier paperwork)
  claimType: claimTypeEnum("claim_type").default("residential"), // Residential (individual) or Commercial (business)
  policyholderFirstName: varchar("policyholder_first_name"), // Required for residential, null for commercial
  policyholderLastName: varchar("policyholder_last_name"), // Required for residential, null for commercial
  policyholderCompany: varchar("policyholder_company"), // Required for commercial, null for residential
  pointOfContact: varchar("point_of_contact"), // For commercial claims - the contact person at the company
  policyholderEmail: varchar("policyholder_email"),
  policyholderPhone: varchar("policyholder_phone"),
  policyholderNotes: text("policyholder_notes"),
  
  // Policyholder 2 Information (for joint policyholders - optional)
  policyholder2FirstName: varchar("policyholder2_first_name"),
  policyholder2LastName: varchar("policyholder2_last_name"),
  policyholder2Email: varchar("policyholder2_email"),
  policyholder2Phone: varchar("policyholder2_phone"),
  
  // Property Information
  propertyAddress: text("property_address").notNull(),
  propertyCity: varchar("property_city").notNull(),
  propertyState: varchar("property_state").notNull(),
  propertyZip: varchar("property_zip").notNull(),

  // Policyholder mailing address (if different from property — for certified mail via Letterstream)
  phMailingAddress: text("ph_mailing_address"),
  phMailingCity: varchar("ph_mailing_city"),
  phMailingState: varchar("ph_mailing_state"),
  phMailingZip: varchar("ph_mailing_zip"),

  propertyType: propertyTypeEnum("property_type"),
  yearBuilt: integer("year_built"),
  lenderName: varchar("lender_name"), // Mortgage or LOC lender name for PA agreements
  mortgageLoanNumber: varchar("mortgage_loan_number"), // Mortgage/LOC loan number
  
  // Enhanced Status Tracking (from current CRM analysis)
  insuranceNotified: boolean("insurance_notified").default(false),
  insuranceNotifiedDate: date("insurance_notified_date"),
  proofOfLossSent: boolean("proof_of_loss_sent").default(false),
  proofOfLossSentDate: date("proof_of_loss_sent_date"),
  inspectionStatus: inspectionStatusEnum("inspection_status").default("not_scheduled"),
  inspectionDate: date("inspection_date"),
  inspectionCompletedDate: date("inspection_completed_date"),
  contractorFileNumber: varchar("contractor_file_number"),
  
  // Statute and Compliance Tracking
  proofOfLossSubmittedDate: date("proof_of_loss_submitted_date"), // When actually submitted to carrier
  carrierResponseDate: date("carrier_response_date"), // When carrier responded
  carrierResponseType: varchar("carrier_response_type"), // "accepted", "denied", "partial", "pending"
  depreciationRecoveryDeadline: date("depreciation_recovery_deadline"), // Policy-specific deadline
  statuteLimitationsDate: date("statute_limitations_date"), // State-specific filing deadline
  appraisalDeadlineDate: date("appraisal_deadline_date"), // State-specific appraisal deadline
  carrierViolationStartDate: date("carrier_violation_start_date"), // 30 days after proof of loss
  isCarrierInViolation: boolean("is_carrier_in_violation").default(false),
  strategyNotes: text("strategy_notes"), // AI or manual strategy recommendations
  
  // AI Review Tracking
  lastAiReviewAt: timestamp("last_ai_review_at"),
  lastWeeklyDraftAt: timestamp("last_weekly_draft_at"),
  lastLitigationDraftAt: timestamp("last_litigation_draft_at"),

  // Enhanced Loss Details
  lossDescription: text("loss_description"), // More detailed than general description
  propertyHabitable: boolean("property_habitable").default(true),
  initialValue: decimal("initial_value", { precision: 12, scale: 2 }),
  
  // PA Agreement Financial Terms
  paAgreementCompanyFeeType: feeTypeEnum("pa_agreement_company_fee_type"), // null treated as 'percent'
  paAgreementCompanyFeePercentage: decimal("pa_agreement_company_fee_percentage", { precision: 5, scale: 2 }), // Company fee % from PA agreement (e.g., 10.00 for 10%)
  paAgreementCompanyFlatAmount: decimal("pa_agreement_company_flat_amount", { precision: 12, scale: 2 }),
  paAgreementCompanyHourlyRate: decimal("pa_agreement_company_hourly_rate", { precision: 8, scale: 2 }),
  feeBasedOnNetIncrease: boolean("fee_based_on_net_increase").default(false), // If true, fee is based on net increase (settlement - initial value)
  mplcEngagementDate: date("mplc_engagement_date"), // Date MPLC was engaged (for determining initial value baseline)
  
  // Carrier Paperwork Provenance
  carrierPaperworkReceived: boolean("carrier_paperwork_received").default(false),
  carrierPaperworkDate: date("carrier_paperwork_date"),
  paperworkSource: varchar("paperwork_source"), // "email", "fax", "portal", "mail"
  paperworkReferenceNumber: varchar("paperwork_reference_number"),
  dateOfLossVerified: boolean("date_of_loss_verified").default(false),
  dateOfLossVerificationNotes: text("date_of_loss_verification_notes"),
  
  // Referrer/Contractor who referred this claim
  referrerId: uuid("referrer_id").references(() => contacts.id),

  // Attorney assigned to this claim (Admin / Office Manager only)
  attorneyId: uuid("attorney_id").references(() => contacts.id),
  sentToAttorneyDate: date("sent_to_attorney_date"),
  
  // QuickBooks Online Integration - Policyholder synced as Customer
  qboCustomerId: varchar("qbo_customer_id"), // QBO Customer ID when synced
  qboSyncedAt: timestamp("qbo_synced_at"), // Last successful sync timestamp

  // Track who created the claim (for access control when PA assignments change)
  createdBy: varchar("created_by").references(() => users.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});


// Contractors table
export const contractors = pgTable("contractors", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),
  firstName: varchar("first_name").notNull(),
  lastName: varchar("last_name").notNull(),
  email: varchar("email"),
  phone: varchar("phone"),
  fileNumber: varchar("file_number"), // Contractor File Number
  company: varchar("company"),
  address: text("address"),
  city: varchar("city"),
  state: varchar("state"),
  zip: varchar("zip"),
  specialties: text("specialties"), // Types of work they specialize in
  licenseNumber: varchar("license_number"),
  notes: text("notes"),

  // QuickBooks Online Integration
  qboVendorId: varchar("qbo_vendor_id"), // QBO Vendor ID when synced
  qboSyncedAt: timestamp("qbo_synced_at"), // Last successful sync timestamp

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_contractors_file_number").on(table.fileNumber),
  index("idx_contractors_company").on(table.company),
  index("idx_contractors_org_id").on(table.orgId),
]);


// Claim contacts junction table
export const claimContacts = pgTable("claim_contacts", {
  claimId: uuid("claim_id").notNull().references(() => claims.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  isPrimary: boolean("is_primary").default(false),
  relationship: varchar("relationship"), // claimant, witness, contractor, etc.
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.claimId, table.contactId] }),
}));


// Claim adjusters junction table for assigning multiple adjusters to claims
export const claimAdjusters = pgTable("claim_adjusters", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  adjusterId: varchar("adjuster_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  
  // Assignment details
  assignmentRole: varchar("assignment_role").notNull().default("adjuster"), // lead_adjuster, supporting_adjuster, consultant, appraiser, specialist
  isPrimary: boolean("is_primary").default(false), // Mark primary/lead adjuster
  isActive: boolean("is_active").default(true), // Active assignment status
  
  // Assignment dates
  assignedAt: timestamp("assigned_at").defaultNow().notNull(),
  startDate: timestamp("start_date"), // When work should begin
  endDate: timestamp("end_date"), // When assignment ends (optional)
  
  // Assignment tracking
  assignedBy: varchar("assigned_by").references(() => users.id).notNull(), // Who made the assignment
  assignmentReason: text("assignment_reason"), // Why this adjuster was assigned
  specialInstructions: text("special_instructions"), // Any special instructions
  
  // Status tracking
  acceptanceStatus: varchar("acceptance_status").default("pending"), // pending, accepted, declined
  acceptedAt: timestamp("accepted_at"),
  declinedAt: timestamp("declined_at"),
  declinedReason: text("declined_reason"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_claim_adjusters_claim_id").on(table.claimId),
  index("idx_claim_adjusters_adjuster_id").on(table.adjusterId),
  index("idx_claim_adjusters_is_active").on(table.isActive),
  index("idx_claim_adjusters_is_primary").on(table.isPrimary),
  index("idx_claim_adjusters_assignment_role").on(table.assignmentRole),
  index("idx_claim_adjusters_assigned_by").on(table.assignedBy),
]);


// Claim PA Assignments table - for tracking PA assignments and commission splits
export const claimPaAssignments = pgTable("claim_pa_assignments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }), // Null if custom/external PA
  
  // PA details
  role: paRoleEnum("role").notNull(), // sales_pa, field_pa, desk_pa, other
  customRoleName: varchar("custom_role_name"), // Used when role is "other"
  paName: varchar("pa_name"), // Fallback name if userId is null
  
  // Commission split
  commissionFeeType: feeTypeEnum("commission_fee_type").default("percent"), // percent, flat_rate, or hourly
  commissionPercentage: decimal("commission_percentage", { precision: 5, scale: 2 }).notNull(), // Percentage of company fee (used when feeType is percent)
  commissionFlatAmount: decimal("commission_flat_amount", { precision: 12, scale: 2 }), // Flat dollar amount (used when feeType is flat_rate)
  commissionHourlyRate: decimal("commission_hourly_rate", { precision: 8, scale: 2 }), // Hourly rate (used when feeType is hourly)
  
  // Metadata
  notes: text("notes"),
  isActive: boolean("is_active").default(true),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_claim_pa_assignments_claim").on(table.claimId),
  index("idx_claim_pa_assignments_user").on(table.userId),
  index("idx_claim_pa_assignments_role").on(table.role),
]);


// Policyholder status updates table for professional claim updates
export const policyholderUpdates = pgTable("policyholder_updates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  content: text("content").notNull(), // Main update content
  keyHighlights: jsonb("key_highlights"), // Array of key highlights
  nextSteps: jsonb("next_steps"), // Array of next steps
  coversPeriod: varchar("covers_period"), // Date range covered
  includeWeeks: integer("include_weeks").default(3), // Weeks included in the update
  tone: varchar("tone").default("professional"), // professional, reassuring, confident
  generatedBy: varchar("generated_by").references(() => users.id),
  sentToPolicyholder: boolean("sent_to_policyholder").default(false),
  sentAt: timestamp("sent_at"),
  sentBy: varchar("sent_by").references(() => users.id),
  
  // Referrer/Contractor update tracking
  sentToReferrer: boolean("sent_to_referrer").default(false),
  sentToReferrerAt: timestamp("sent_to_referrer_at"),
  sentToReferrerEmail: varchar("sent_to_referrer_email"), // Email it was sent to
  referrerSummary: text("referrer_summary"), // Sanitized version for referrer (no financials)

  // Contractor flip fields — populated when status is contractor_in_production_admin
  contractorEmail: varchar("contractor_email"),    // Recipient email for contractor flip updates
  subjectOverride: varchar("subject_override"),    // Custom subject line for contractor flip emails

  // Email draft links (raw uuid — no .references() to avoid circular import with communications.ts)
  emailDraftId: uuid("email_draft_id"),         // PH draft ID
  referrerEmailDraftId: uuid("referrer_email_draft_id"), // Referrer draft ID

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_policyholder_updates_claim_id").on(table.claimId),
  index("idx_policyholder_updates_created_at").on(table.createdAt),
]);



// Timeline events table  
export const timelineEvents = pgTable("timeline_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  type: timelineEventTypeEnum("type").notNull(),
  occurredAt: timestamp("occurred_at").defaultNow(),
  actorId: varchar("actor_id").references(() => users.id),
  summary: varchar("summary").notNull(),
  details: jsonb("details"), // Structured event data
  relatedDocumentId: uuid("related_document_id").references(() => documents.id),
  relatedContactId: uuid("related_contact_id").references(() => contacts.id),
  relatedTaskId: uuid("related_task_id").references(() => tasks.id),
  isSystem: boolean("is_system").default(false), // System-generated vs user action
  mentions: jsonb("mentions").$type<Array<{ userId: string; offset: number; length: number }>>(),
  createdAt: timestamp("created_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_timeline_events_claim_id").on(table.claimId),
  index("idx_timeline_events_occurred_at").on(table.occurredAt),
  index("idx_timeline_events_type").on(table.type),
]);


// Attorney exclusions table — marks timeline items to exclude from attorney export
export const attorneyExclusions = pgTable("attorney_exclusions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  entityType: varchar("entity_type").notNull(), // 'timeline_event' | 'communication' | 'inspection'
  entityId: uuid("entity_id").notNull(),
  excludedBy: varchar("excluded_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_attorney_exclusions_claim_id").on(table.claimId),
  uniqueIndex("idx_attorney_exclusions_unique").on(table.claimId, table.entityType, table.entityId),
]);


// Inspections table with Google Calendar integration
export const inspections = pgTable("inspections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Inspection details
  type: varchar("type").notNull(), // initial, engineer, building_consultant, adjuster
  title: varchar("title").notNull(),
  description: text("description"),
  status: inspectionStatusEnum("status").default("not_scheduled"),
  
  // Scheduling information
  scheduledDate: timestamp("scheduled_date"),
  scheduledStartTime: timestamp("scheduled_start_time"),
  scheduledEndTime: timestamp("scheduled_end_time"),
  actualStartTime: timestamp("actual_start_time"),
  actualEndTime: timestamp("actual_end_time"),
  
  // Location information
  location: text("location"), // Property address or custom location
  meetingPoint: text("meeting_point"), // Specific meeting instructions
  
  // Attendees
  inspectorUserId: varchar("inspector_user_id").references(() => users.id),
  attendeeEmails: jsonb("attendee_emails"), // Array of email addresses
  attendeeNotes: text("attendee_notes"), // Special instructions for attendees
  
  // Meeting/Call information
  isVirtualMeeting: boolean("is_virtual_meeting").default(false),
  meetingLink: text("meeting_link"), // Zoom, Teams, etc.
  dialInNumber: varchar("dial_in_number"),
  meetingId: varchar("meeting_id"),
  
  // Documentation and results
  completionNotes: text("completion_notes"),
  documentsGenerated: jsonb("documents_generated"), // Array of document IDs created from this inspection
  nextSteps: jsonb("next_steps"), // Array of follow-up actions
  
  // Weather and conditions (for field inspections)
  weatherConditions: varchar("weather_conditions"),
  accessIssues: text("access_issues"),
  safetyNotes: text("safety_notes"),
  
  // Rescheduling tracking
  originalScheduledDate: timestamp("original_scheduled_date"),
  rescheduleCount: integer("reschedule_count").default(0),
  rescheduleReason: text("reschedule_reason"),
  
  // Creation and modification tracking
  scheduledBy: varchar("scheduled_by").references(() => users.id),
  lastModifiedBy: varchar("last_modified_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_inspections_claim_id").on(table.claimId),
  index("idx_inspections_scheduled_date").on(table.scheduledDate),
  index("idx_inspections_status").on(table.status),
  index("idx_inspections_type").on(table.type),
  index("idx_inspections_inspector").on(table.inspectorUserId),
]);


// Claims evaluation table for automatic evaluation workflow
export const claimsEvaluations = pgTable("claims_evaluations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  evaluationType: evaluationTypeEnum("evaluation_type").notNull(),
  status: evaluationStatusEnum("status").default("pending"),
  
  // Policy Analysis Results
  deductibleAmount: decimal("deductible_amount", { precision: 12, scale: 2 }),
  policyLimits: decimal("policy_limits", { precision: 12, scale: 2 }),
  exclusionsFound: jsonb("exclusions_found"), // Array of exclusion types and details
  coverageApplies: boolean("coverage_applies"),
  policyAnalysisNotes: text("policy_analysis_notes"),
  
  // Licensing Validation
  stateOfLoss: varchar("state_of_loss").notNull(),
  licenseStatus: licenseStatusEnum("license_status").notNull(),
  licenseValidUntil: timestamp("license_valid_until"),
  canProvideServices: boolean("can_provide_services").default(false),
  licensingNotes: text("licensing_notes"),
  
  // Financial Analysis
  contractorEstimate: decimal("contractor_estimate", { precision: 12, scale: 2 }),
  insuranceEstimate: decimal("insurance_estimate", { precision: 12, scale: 2 }),
  estimateDifference: decimal("estimate_difference", { precision: 12, scale: 2 }),
  potentialRecovery: decimal("potential_recovery", { precision: 12, scale: 2 }),
  feeEstimate: decimal("fee_estimate", { precision: 12, scale: 2 }),
  
  // Evaluation Criteria Scores (0-100)
  claimViabilityScore: integer("claim_viability_score"),
  complexityScore: integer("complexity_score"),
  documentationScore: integer("documentation_score"),
  timelinessScore: integer("timeliness_score"),
  overallScore: integer("overall_score"),
  
  // Workflow Decision
  recommendedAction: varchar("recommended_action"), // accept, reject, request_more_info, consultation
  autoApproved: boolean("auto_approved").default(false),
  requiresManualReview: boolean("requires_manual_review").default(false),
  rejectionReasons: jsonb("rejection_reasons"), // Array of rejection reasons
  
  // Communication Tracking
  educationEmailsSent: integer("education_emails_sent").default(0),
  callsScheduled: integer("calls_scheduled").default(0),
  lastCommunicationAt: timestamp("last_communication_at"),
  
  evaluatedBy: varchar("evaluated_by").references(() => users.id),
  evaluatedAt: timestamp("evaluated_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_claims_evaluations_claim_id").on(table.claimId),
  index("idx_claims_evaluations_status").on(table.status),
  index("idx_claims_evaluations_state").on(table.stateOfLoss),
  index("idx_claims_evaluations_score").on(table.overallScore),
]);


// Intake Decisions - PA vs Appraisal routing logic
// Used primarily for lead evaluation (pre-conversion to claim)
export const intakeDecisions = pgTable("intake_decisions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  leadId: uuid("lead_id").references(() => leads.id, { onDelete: "cascade" }),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }), // Legacy support - now optional
  
  // Geographic and licensing
  state: varchar("state", { length: 2 }).notNull(), // US two-letter state code
  city: varchar("city").notNull(),
  mplcLicensedInState: boolean("mplc_licensed_in_state").notNull(),
  isRestrictedScopeState: boolean("is_restricted_scope_state").notNull(), // AL, AR, AK, GA, KS, MA, MD, MS, MO, NV, NH, NJ, NC, PA, RI, SC, TN, UT, WA, WI
  
  // Financial
  lossAmount: decimal("loss_amount", { precision: 12, scale: 2 }).notNull(),
  paMinimumThreshold: decimal("pa_minimum_threshold", { precision: 12, scale: 2 }).notNull(), // $100k or $500k based on city
  meetsFinancialThreshold: boolean("meets_financial_threshold").notNull(),
  
  // Claim characteristics  
  isCommercialOrLargeResidential: boolean("is_commercial_or_large_residential").notNull(),
  peril: perilTypeEnum("peril").notNull(),
  
  // Coverage and scope
  coverageOpened: boolean("coverage_opened").notNull(), // Carrier acknowledged coverage for disputed items
  valuationOnlyDispute: boolean("valuation_only_dispute").notNull(), // Both sides agree on coverage, only price differs
  contractorScopeMatchesCarrier: boolean("contractor_scope_matches_carrier").notNull(), // Scope-match pathway: same quantities, different pricing
  roofShinglesIncludedInEstimate: boolean("roof_shingles_included_in_estimate"),
  
  // Negotiation context
  negotiationStatus: negotiationStatusEnum("negotiation_status").notNull(),
  
  // Document-derived features with confidence scores
  mentionsCoverageDenial: boolean("mentions_coverage_denial").default(false),
  mentionsExclusionOrLimitedCoverage: boolean("mentions_exclusion_or_limited_coverage").default(false),
  mentionsAppraisalClauseInvoked: boolean("mentions_appraisal_clause_invoked").default(false),
  carrierEstimatePresent: boolean("carrier_estimate_present").default(false),
  scopeShinglesExcludedOnRoofClaim: boolean("scope_shingles_excluded_on_roof_claim").default(false),
  settlementNegotiationsHistory: varchar("settlement_negotiations_history"), // none, some, failed
  policyInterpretationFlags: boolean("policy_interpretation_flags").default(false),
  documentDerivedConfidence: integer("document_derived_confidence").default(0), // 0-100 AI confidence
  
  // Decision outputs
  decisionPathway: intakeDecisionPathwayEnum("decision_pathway").notNull(),
  paAllowed: boolean("pa_allowed").notNull(),
  appraisalAllowed: boolean("appraisal_allowed").notNull(),
  disqualificationReasons: jsonb("disqualification_reasons"), // Array of reasons
  recommendedService: varchar("recommended_service"), // "public_adjuster", "appraisal", "both", "none"
  
  // Business logic explanations
  decisionRationale: text("decision_rationale"), // Human-readable explanation
  jurisdictionalNotes: text("jurisdictional_notes"), // State-specific considerations
  scopeMatchPathwayApplicable: boolean("scope_match_pathway_applicable").default(false), // Can use scope-match in restricted state
  
  // Lead time estimates
  estimatedLeadTimeDays: integer("estimated_lead_time_days"), // For IA/LA registration ~14 days
  
  // Review and override
  requiresManualReview: boolean("requires_manual_review").default(false),
  manualReviewNotes: text("manual_review_notes"),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  decisionOverridden: boolean("decision_overridden").default(false),
  overrideReason: text("override_reason"),
  
  // Metadata
  dateOfLoss: date("date_of_loss"),
  notes: text("notes"),
  evaluatedBy: varchar("evaluated_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_intake_decisions_lead_id").on(table.leadId),
  index("idx_intake_decisions_claim_id").on(table.claimId),
  index("idx_intake_decisions_state").on(table.state),
  index("idx_intake_decisions_decision_pathway").on(table.decisionPathway),
  index("idx_intake_decisions_pa_allowed").on(table.paAllowed),
  index("idx_intake_decisions_appraisal_allowed").on(table.appraisalAllowed),
  index("idx_intake_decisions_requires_review").on(table.requiresManualReview),
  index("idx_intake_decisions_created_at").on(table.createdAt),
]);


// Policy analysis results table for detailed policy examination
export const policyAnalysis = pgTable("policy_analysis", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  documentId: uuid("document_id").references(() => documents.id), // Reference to policy document
  
  // Coverage Analysis
  dwellingCoverage: decimal("dwelling_coverage", { precision: 12, scale: 2 }),
  personalPropertyCoverage: decimal("personal_property_coverage", { precision: 12, scale: 2 }),
  additionalLivingExpenses: decimal("additional_living_expenses", { precision: 12, scale: 2 }),
  deductibleAmount: decimal("deductible_amount", { precision: 12, scale: 2 }),
  
  // Exclusions Found
  exclusions: jsonb("exclusions"), // Detailed exclusions with text and implications
  applicableExclusions: jsonb("applicable_exclusions"), // Exclusions that apply to this claim
  
  // Special Conditions
  windHailDeductible: decimal("wind_hail_deductible", { precision: 12, scale: 2 }),
  namedStormDeductible: decimal("named_storm_deductible", { precision: 12, scale: 2 }),
  cosmeticExclusionApplies: boolean("cosmetic_exclusion_applies").default(false),
  windDrivenRainExclusion: boolean("wind_driven_rain_exclusion").default(false),
  
  // Policy Details
  policyEffectiveDate: timestamp("policy_effective_date"),
  policyExpirationDate: timestamp("policy_expiration_date"),
  policyType: varchar("policy_type"), // HO3, HO6, DP3, etc.
  carrier: varchar("carrier"),
  
  // Suit Limitation (SOL) - from policy "Suit Against Us" / "Legal Action Against Us" clause
  suitLimitationYears: decimal("suit_limitation_years", { precision: 5, scale: 2 }), // e.g. 2, 1.5, 5
  suitLimitationClauseText: text("suit_limitation_clause_text"), // exact policy language
  
  // AI Analysis Results
  analysisConfidence: decimal("analysis_confidence", { precision: 5, scale: 2 }), // 0-100%
  recommendedCoverage: decimal("recommended_coverage", { precision: 12, scale: 2 }),
  analysisNotes: text("analysis_notes"),
  flaggedConcerns: jsonb("flagged_concerns"), // Issues requiring human review
  
  analyzedBy: varchar("analyzed_by"), // 'ai' or user ID
  analyzedAt: timestamp("analyzed_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_policy_analysis_claim_id").on(table.claimId),
  index("idx_policy_analysis_document_id").on(table.documentId),
]);


// Questionnaire Templates and Responses
export const questionnaireTemplates = pgTable('questionnaire_templates', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  name: varchar('name', { length: 255 }).notNull(),
  description: text('description'),
  questions: jsonb('questions').notNull().$type<QuestionnaireQuestion[]>(),
  isActive: boolean('is_active').default(true).notNull(),
  createdBy: varchar('created_by', { length: 255 }).references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull()
});


export const questionnaireResponses = pgTable('questionnaire_responses', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid('claim_id').references(() => claims.id, { onDelete: 'cascade' }).notNull(),
  templateId: uuid('template_id').references(() => questionnaireTemplates.id).notNull(),
  respondentEmail: varchar('respondent_email', { length: 255 }).notNull(),
  respondentName: varchar('respondent_name', { length: 255 }),
  responses: jsonb('responses').notNull().$type<QuestionnaireAnswer[]>(),
  status: varchar('status', { length: 50 }).notNull().default('pending'), // pending, in_progress, completed
  sentAt: timestamp('sent_at'),
  startedAt: timestamp('started_at'),
  completedAt: timestamp('completed_at'),
  remindersSent: integer('reminders_sent').default(0),
  lastReminderAt: timestamp('last_reminder_at'),
  accessToken: varchar('access_token', { length: 255 }).unique(), // Secure token for accessing form
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull()
});


// Type definitions for questionnaire questions and answers
export interface QuestionnaireQuestion {
  id: string;
  question: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'radio' | 'checkbox' | 'date' | 'number' | 'email' | 'phone' | 'file' | 'address' | 'yesno';
  required: boolean;
  options?: string[];
  category?: string;
  helpText?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  conditional?: {
    questionId: string;
    value: any;
  };
}


export interface QuestionnaireAnswer {
  questionId: string;
  answer: any;
  answeredAt: string;
}


// Claim Coverage Breakdowns for RCV and Depreciation tracking
export const claimCoverages = pgTable("claim_coverages", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Coverage values
  coverageType: policyCoverageEnum("coverage_type").notNull(),
  customCoverageName: varchar("custom_coverage_name"), // For custom/additional coverages
  rcv: decimal("rcv", { precision: 12, scale: 2 }).default('0'), // Replacement Cost Value
  depreciation: decimal("depreciation", { precision: 12, scale: 2 }).default('0'), // Recoverable Depreciation
  acv: decimal("acv", { precision: 12, scale: 2 }).default('0'), // Actual Cash Value (RCV - Depreciation)
  deductible: decimal("deductible", { precision: 12, scale: 2 }).default('0'), // Coverage-specific deductible
  
  // Payment tracking
  initialPayment: decimal("initial_payment", { precision: 12, scale: 2 }).default('0'), // First payment received
  supplementPayment1: decimal("supplement_payment_1", { precision: 12, scale: 2 }).default('0'), // First supplement
  supplementPayment2: decimal("supplement_payment_2", { precision: 12, scale: 2 }).default('0'), // Second supplement
  totalPaid: decimal("total_paid", { precision: 12, scale: 2 }).default('0'), // Sum of all payments
  
  // Tracking
  isManualEntry: boolean("is_manual_entry").default(false), // True if manually entered, false if from carrier estimate
  sourceDocument: uuid("source_document").references(() => documents.id), // Document where values were extracted from
  notes: text("notes"),
  
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_claim_coverages_claim_id").on(table.claimId),
  index("idx_claim_coverages_type").on(table.coverageType),
]);


// =====================
// Claim Activity Tracking
// =====================

// Track last activity on claims and identify stale claims needing follow-up
export const claimActivityTracking = pgTable("claim_activity_tracking", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull().unique(),
  
  // Activity timestamps
  lastActivityAt: timestamp("last_activity_at").notNull(),
  lastActivityType: varchar("last_activity_type"), // communication, document, task, inspection, etc.
  lastActivityDescription: text("last_activity_description"),
  daysSinceLastActivity: integer("days_since_last_activity").notNull().default(0),
  
  // Inactivity thresholds and status
  inactivityThresholdDays: integer("inactivity_threshold_days").notNull().default(5),
  isStale: boolean("is_stale").default(false), // True if days since activity > threshold
  staleDetectedAt: timestamp("stale_detected_at"),
  
  // Follow-up recommendations
  needsFollowUp: boolean("needs_follow_up").default(false),
  followUpRecommendation: text("follow_up_recommendation"), // AI-generated recommendation
  followUpPriority: varchar("follow_up_priority").default("medium"), // low, medium, high, urgent
  followUpGeneratedAt: timestamp("follow_up_generated_at"),
  followUpGeneratedBy: varchar("follow_up_generated_by"), // system_ai or user ID
  
  // Follow-up completion tracking
  followUpCompletedAt: timestamp("follow_up_completed_at"),
  followUpCompletedBy: varchar("follow_up_completed_by").references(() => users.id),
  followUpNotes: text("follow_up_notes"),
  
  // Future Twilio integration fields
  callScheduledFor: timestamp("call_scheduled_for"),
  callAttempts: integer("call_attempts").default(0),
  lastCallAt: timestamp("last_call_at"),
  callStatus: varchar("call_status"), // scheduled, in_progress, completed, failed, no_answer
  callTranscript: text("call_transcript"),
  callDuration: integer("call_duration"), // seconds
  
  // Metadata
  metadata: jsonb("metadata"), // Additional tracking data
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_activity_tracking_claim_id").on(table.claimId),
  index("idx_activity_tracking_is_stale").on(table.isStale),
  index("idx_activity_tracking_needs_followup").on(table.needsFollowUp),
  index("idx_activity_tracking_last_activity").on(table.lastActivityAt),
  index("idx_activity_tracking_days_since").on(table.daysSinceLastActivity),
  index("idx_activity_tracking_priority").on(table.followUpPriority),
  index("idx_activity_tracking_call_scheduled").on(table.callScheduledFor),
]);


// Claim Package Generations - Track document package generation per claim
export const claimPackageGenerations = pgTable("claim_package_generations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // References
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  packageId: uuid("package_id").references(() => documentTemplatePackages.id).notNull(),
  packageVersionId: uuid("package_version_id").references(() => packageVersions.id),
  feeConfirmationId: uuid("fee_confirmation_id").references(() => feeConfirmations.id),
  
  // Generation details
  status: packageGenerationStatusEnum("status").notNull().default("pending"),
  statusMessage: text("status_message"),
  
  // Validation results
  validationPassed: boolean("validation_passed").default(false),
  validationErrors: jsonb("validation_errors"), // Array of {field, message, severity}
  validationWarnings: jsonb("validation_warnings"), // Non-blocking warnings
  validatedAt: timestamp("validated_at"),
  
  // Generated documents
  generatedDocuments: jsonb("generated_documents"), // Array of {documentGenerationId, templateId, order, status}
  documentsGeneratedAt: timestamp("documents_generated_at"),
  
  // DocuSign integration
  docusignEnvelopeId: uuid("docusign_envelope_id").references(() => docusignEnvelopes.id),
  sentToDocusignAt: timestamp("sent_to_docusign_at"),
  allSignaturesCollectedAt: timestamp("all_signatures_collected_at"),
  
  // Completion tracking
  completedAt: timestamp("completed_at"),
  cancelledAt: timestamp("cancelled_at"),
  cancelledBy: varchar("cancelled_by").references(() => users.id),
  cancelledReason: text("cancelled_reason"),
  
  // Metadata
  metadata: jsonb("metadata"), // Additional tracking info
  
  generatedBy: varchar("generated_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_claim_package_generations_claim_id").on(table.claimId),
  index("idx_claim_package_generations_package_id").on(table.packageId),
  index("idx_claim_package_generations_status").on(table.status),
  index("idx_claim_package_generations_created_at").on(table.createdAt),
]);


// Claim Compliance Items - Per-claim tracking of each compliance deadline
export const claimComplianceItems = pgTable("claim_compliance_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Claim reference
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Rule reference
  sourceRuleId: uuid("source_rule_id").references(() => stateComplianceRules.id),
  requirementType: complianceRequirementTypeEnum("requirement_type").notNull(),
  
  // Dates
  triggerDate: timestamp("trigger_date"), // When the clock started
  dueDate: timestamp("due_date"), // Calculated deadline
  completedDate: timestamp("completed_date"), // When requirement was met
  
  // Status (computed dynamically, but stored for filtering)
  status: complianceStatusEnum("status").notNull().default("not_triggered"),
  
  // Recurrence tracking
  recurrenceCount: integer("recurrence_count").default(0), // For recurring requirements
  nextRecurrenceDate: timestamp("next_recurrence_date"),
  
  // Override/manual adjustments
  isManualOverride: boolean("is_manual_override").default(false),
  overrideReason: text("override_reason"),
  overrideByUserId: varchar("override_by_user_id").references(() => users.id),
  overrideDate: timestamp("override_date"),
  
  // Evidence linking
  linkedDocumentIds: jsonb("linked_document_ids"), // Documents proving completion
  linkedTimelineEventIds: jsonb("linked_timeline_event_ids"), // Timeline events proving completion
  linkedCommunicationIds: jsonb("linked_communication_ids"), // Communications proving completion
  
  // Notes
  notes: text("notes"),
  carrierResponse: text("carrier_response"), // What carrier said/did
  
  // Alert tracking
  alertSent: boolean("alert_sent").default(false),
  alertSentDate: timestamp("alert_sent_date"),
  escalationSent: boolean("escalation_sent").default(false),
  escalationSentDate: timestamp("escalation_sent_date"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_claim_compliance_items_claim").on(table.claimId),
  index("idx_claim_compliance_items_status").on(table.status),
  index("idx_claim_compliance_items_due_date").on(table.dueDate),
  index("idx_claim_compliance_items_type").on(table.requirementType),
]);


// Claim Links - user-defined links (label + value) associated with a claim
export const claimLinks = pgTable("claim_links", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  label: varchar("label", { length: 255 }).notNull(),
  value: text("value").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_claim_links_claim_id").on(table.claimId),
]);
